import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getSharedList, 
  pollForChanges, 
  updateShoppingListItem, 
  deleteShoppingListItem,
  createShoppingListItem
} from '../services/api';
import { API_URL } from '../config';
import '../styles/SharedList.css';

const SharedList = () => {
  const { shareId } = useParams();
  const [sharedList, setSharedList] = useState(null);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: '', quantity: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollInterval, setPollInterval] = useState(5000); // 5 seconds
  const pollingRef = useRef(null);
  const navigate = useNavigate();
  
  // Sort orders for days and meal types
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Ungrouped'];
  const mealOrder = ['Breakfast', 'Lunch', 'Dinner', 'Other'];

  // State for fallback mode (local copy only)
  const [isLocalOnly, setIsLocalOnly] = useState(false);
  const [localShareId, setLocalShareId] = useState('');

  // New state for join mode
  const [joinMode, setJoinMode] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  // New state for showCopiedMessage
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  useEffect(() => {
    const fetchSharedList = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/shared-lists/by_share_id/?id=${shareId}`);
        const data = await response.json();
        
        if (response.ok) {
          setSharedList(data);
        } else {
          // Check if this is a fallback response
          if (data.fallback && data.local_copy) {
            console.log("Using local-only mode for shared list");
            setIsLocalOnly(true);
            setError("Collaborative mode unavailable. Using local copy instead.");
          } else {
            setError(data.error || 'Failed to fetch shared list');
          }
        }
      } catch (err) {
        console.error("Error fetching shared list:", err);
        setError('Network error fetching shared list');
        setIsLocalOnly(true);
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchSharedList();
    }
    
    // Cleanup polling on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [shareId]);
  
  const startPolling = () => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    // Set up polling for real-time updates
    pollingRef.current = setInterval(async () => {
      const hasChanges = await pollForChanges(shareId, items, (newItems) => {
        setItems(newItems);
      });
      
      // If there are frequent changes, speed up polling
      if (hasChanges && pollInterval > 2000) {
        setPollInterval(2000);
        restartPolling();
      } else if (!hasChanges && pollInterval < 5000) {
        // If no changes, slow down polling
        setPollInterval(5000);
        restartPolling();
      }
    }, pollInterval);
  };
  
  const restartPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      startPolling();
    }
  };
  
  const handleToggleComplete = async (item) => {
    try {
      const updatedItem = { ...item, completed: !item.completed };
      await updateShoppingListItem(item.id, updatedItem);
      
      // Update local state immediately for better UX
      setItems(items.map(i => i.id === item.id ? updatedItem : i));
      
      // Force refresh to sync with other users
      const response = await getSharedList(shareId);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };
  
  const handleDeleteItem = async (id) => {
    try {
      await deleteShoppingListItem(id);
      
      // Update local state immediately for better UX
      setItems(items.filter(item => item.id !== id));
      
      // Force refresh to sync with other users
      const response = await getSharedList(shareId);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem({
      ...newItem,
      [name]: value,
    });
  };
  
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;

    try {
      await createShoppingListItem(newItem);
      setNewItem({ name: '', quantity: '' });
      
      // Force refresh to sync with other users
      const response = await getSharedList(shareId);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };
  
  const handleReturnToHome = () => {
    navigate('/');
  };

  // Group items by day and meal type
  const groupItemsByDayAndMeal = (items) => {
    const grouped = {};
    
    items.forEach(item => {
      // Handle missing day or meal_type fields
      if (!item || typeof item !== 'object') {
        return; // Skip invalid items
      }
      
      const day = item.day || 'Ungrouped';
      if (!grouped[day]) {
        grouped[day] = {};
      }
      
      const mealType = item.meal_type || 'Other';
      if (!grouped[day][mealType]) {
        grouped[day][mealType] = [];
      }
      
      grouped[day][mealType].push(item);
    });
    
    return grouped;
  };

  // Create a new shared list
  const createSharedList = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/shared-lists/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: 'My Shared Shopping List',
        }),
      });
      
      const data = await response.json();

      if (response.ok) {
        setSharedList(data);
        navigator.clipboard.writeText(`${window.location.origin}/shared/${data.share_id}`);
        setError(null);
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 3000);
      } else {
        // Check if this is a fallback response
        if (data.fallback && data.local_copy) {
          console.log("Using local-only mode for shared list");
          setIsLocalOnly(true);
          setLocalShareId(data.share_id || 'LOCAL');
          
          // Still copy a text version to clipboard
          const items = await fetchCurrentItems();
          const textList = convertToText(items);
          navigator.clipboard.writeText(textList);
          
          setError("Collaborative lists are temporarily unavailable. A text version has been copied to your clipboard instead.");
          setShowCopiedMessage(true);
          setTimeout(() => setShowCopiedMessage(false), 3000);
        } else {
          setError(data.error || 'Failed to create shared list');
        }
      }
    } catch (err) {
      console.error("Error creating shared list:", err);
      setError('Network error creating shared list');
      
      // Fallback to local clipboard copy
      try {
        const items = await fetchCurrentItems();
        const textList = convertToText(items);
        navigator.clipboard.writeText(textList);
        setError("Network error. A text version has been copied to your clipboard instead.");
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 3000);
      } catch (clipboardErr) {
        console.error("Error copying to clipboard:", clipboardErr);
        setError("Network error creating shared list and copying to clipboard");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch current shopping list items
  const fetchCurrentItems = async () => {
    try {
      const response = await fetch(`${API_URL}/shopping-list/`);
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (err) {
      console.error("Error fetching current items:", err);
      return [];
    }
  };
  
  // Convert items to text format
  const convertToText = (items) => {
    if (!items || items.length === 0) {
      return "Shopping List (Empty)";
    }
    
    // Group items by day and meal
    const grouped = groupItemsByDayAndMeal(items);
    
    let textList = "SHOPPING LIST\n\n";
    
    // Add each day and its items
    Object.keys(grouped).forEach(day => {
      textList += `${day}:\n`;
      
      // Add each meal type and its items
      Object.keys(grouped[day]).forEach(mealType => {
        textList += `  ${mealType}:\n`;
        
        // Add each item
        grouped[day][mealType].forEach(item => {
          const checkmark = item.completed ? "[✓]" : "[ ]";
          textList += `    ${checkmark} ${item.name} ${item.quantity ? `(${item.quantity})` : ""}\n`;
        });
      });
      
      textList += "\n";
    });
    
    return textList;
  };

  // Join an existing shared list
  const joinSharedList = async () => {
    if (!joinCode.trim()) {
      setError('Please enter a share code');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/shared-lists/join_collaborative/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          share_id: joinCode,
        }),
      });
      
      const data = await response.json();

      if (response.ok) {
        setSharedList(data.list);
        setJoinMode(false);
        setJoinCode('');
        setError(null);
      } else {
        // Check if this is a fallback response
        if (data.fallback && data.local_copy) {
          setIsLocalOnly(true);
          setError("Collaborative lists are temporarily unavailable. Using local copy instead.");
          setJoinMode(false);
        } else {
          setError(data.error || 'Failed to join shared list');
        }
      }
    } catch (err) {
      console.error("Error joining shared list:", err);
      setError('Network error joining shared list');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading shared shopping list...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">The link may be invalid or the shared list has expired.</p>
          <button 
            className="btn btn-primary mt-3" 
            onClick={handleReturnToHome}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const activeItems = items.filter(item => !item.completed);
  const completedItems = items.filter(item => item.completed);
  
  if (!sharedList) {
    return (
      <div className="container mt-5">
        <h2>Shared Shopping List</h2>
        
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        
        {!joinMode ? (
          <div className="card">
            <div className="card-body">
              <h2>Collaborative Shopping Lists</h2>
              <p>Work on your shopping list together with others in real-time!</p>
              
              {showCopiedMessage && (
                <div className="success-message">
                  Share link copied to clipboard!
                </div>
              )}
              
              <div className="button-group">
                <button className="btn btn-primary" onClick={createSharedList} disabled={loading}>
                  Create New Collaborative List
                </button>
                <button className="btn btn-secondary" onClick={() => setJoinMode(true)} disabled={loading}>
                  Join Existing List
                </button>
                <button className="btn btn-outline-secondary" onClick={handleReturnToHome} disabled={loading}>
                  Return to Home
                </button>
              </div>
              
              {isLocalOnly && localShareId && (
                <div className="alert alert-info mt-3">
                  <p>Using local mode with ID: {localShareId}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              <h3>Join a Collaborative List</h3>
              <div className="join-form">
                <input
                  type="text"
                  placeholder="Enter share code"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  className="form-control"
                />
                <div className="button-group">
                  <button className="btn btn-primary" onClick={joinSharedList} disabled={loading}>
                    Join List
                  </button>
                  <button className="btn btn-secondary" onClick={() => setJoinMode(false)} disabled={loading}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const groupedItems = groupItemsByDayAndMeal(activeItems);
  const sortedDays = Object.keys(groupedItems).sort((a, b) => 
    dayOrder.indexOf(a) - dayOrder.indexOf(b)
  );

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h2 className="mb-0">{sharedList.title}</h2>
          <div className="d-flex justify-content-between align-items-center">
            <small>Shared on {new Date(sharedList.created_at).toLocaleDateString()}</small>
            <button 
              className="btn btn-sm btn-outline-light" 
              onClick={handleReturnToHome}
            >
              Return to Main App
            </button>
          </div>
        </div>
        <div className="card-body">
          <p>This is a collaborative shopping list - all changes are shared with anyone who has the link.</p>
          <p>Expires on {new Date(sharedList.expires_at).toLocaleDateString()}</p>
          
          {/* Add item form */}
          <form onSubmit={handleAddItem} className="mb-4">
            <div className="row g-3">
              <div className="col-sm-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Item name"
                  name="name"
                  value={newItem.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-sm-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Quantity (e.g., 2 lbs, 3 pcs)"
                  name="quantity"
                  value={newItem.quantity}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-sm-2">
                <button type="submit" className="btn btn-primary w-100">
                  Add
                </button>
              </div>
            </div>
          </form>
          
          {/* Active items */}
          {activeItems.length === 0 ? (
            <div className="alert alert-info">No active items in the shopping list.</div>
          ) : (
            <div>
              {sortedDays.map(day => (
                <div key={day} className="mb-4">
                  <h4 className="border-bottom pb-2">{day}</h4>
                  {Object.keys(groupedItems[day])
                    .sort((a, b) => mealOrder.indexOf(a) - mealOrder.indexOf(b))
                    .map(mealType => (
                      <div key={`${day}-${mealType}`} className="mb-3">
                        <h5 className="text-muted">{mealType}</h5>
                        <ul className="list-group">
                          {groupedItems[day][mealType].map(item => (
                            <li
                              key={item.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={item.completed}
                                  onChange={() => handleToggleComplete(item)}
                                  id={`shared-item-${item.id}`}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`shared-item-${item.id}`}
                                >
                                  <div>
                                    {item.name}
                                    {item.quantity && (
                                      <span className="ms-2 text-muted">({item.quantity})</span>
                                    )}
                                  </div>
                                </label>
                              </div>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          )}
          
          {/* Completed items */}
          {completedItems.length > 0 && (
            <div className="mt-4">
              <h4 className="border-bottom pb-2">Completed Items</h4>
              <ul className="list-group">
                {completedItems.map(item => (
                  <li
                    key={item.id}
                    className="list-group-item d-flex justify-content-between align-items-center text-muted"
                  >
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={item.completed}
                        onChange={() => handleToggleComplete(item)}
                        id={`shared-completed-${item.id}`}
                      />
                      <label
                        className="form-check-label text-decoration-line-through"
                        htmlFor={`shared-completed-${item.id}`}
                      >
                        <div>
                          {item.name}
                          {item.quantity && (
                            <span className="ms-2">({item.quantity})</span>
                          )}
                        </div>
                      </label>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="card-footer d-flex justify-content-between">
          <button 
            className="btn btn-success" 
            onClick={() => window.print()}
          >
            Print Shopping List
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              alert('Shopping list link copied to clipboard! Share it with anyone you want to collaborate with.');
            }}
          >
            Copy Link to Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharedList; 